import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { Form } from "../../../components/Form";
import FormPhoneInput from "../../../components/Form/FormPhoneInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
  FormFieldValue,
} from "../../../components/Form/FormContext";
import {
  sendTwoFactorCode,
  sendRegistrationPhoneCode,
} from "../../../store/actions/authActions";
import { ROUTES, STORAGE_KEYS, COOKIE_KEYS } from "../../../constants";
import { getCookie, setCookie } from "../../../utils/cookieUtils";
import { ValidationResult } from "../../../hooks/useFormValidation";
import {
  TwoFactorContainer,
  TwoFactorIcon,
  TwoFactorTitle,
  TwoFactorSubtitle,
  TwoFactorActions,
  SmsConsentText,
  SmsConsentLinks,
  SmsConsentLink,
} from "./TwoFactorAuthPage.styled";
import images from "../../../assets";
import { validatePhoneNumber, cleanPhoneNumber } from "../../../utils/phoneValidation";
import { useApiErrorHandling } from "../../../hooks";

interface TwoFactorFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
  isSubmitting: boolean;
}

const TwoFactorForm: React.FC<TwoFactorFormProps> = ({
  isSubmitting,
  apiErrors,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const { validationResult } = useFormContext();

  // Disable submit when client-side validation reports errors
  const isFormValid =
    !!validationResult?.isValid &&
    Array.isArray(validationResult?.errorMessages) &&
    validationResult.errorMessages.length === 0;

  return (
    <AuthPageLayout withHeaderImage>
      <TwoFactorContainer>
        <TwoFactorIcon>
          <Box
            component="img"
            src={images.lockedDisplay}
            alt="Success icon"
            sx={{ width: "156px" }}
          />
        </TwoFactorIcon>

        <TwoFactorTitle>{t("twoFactor.title")}</TwoFactorTitle>

        <TwoFactorSubtitle>{t("twoFactor.subtitle")}</TwoFactorSubtitle>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
          }}
        >
          <FormPhoneInput
            name="phoneNumber"
            label={t("twoFactor.phoneNumber")}
            placeholder={t("twoFactor.phoneNumberPlaceholder")}
            required
            fullWidth
          />

          <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

          <TwoFactorActions>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isFormValid}
              fullWidth
            >
              {isSubmitting ? t("twoFactor.sending") : t("twoFactor.sendCode")}
            </Button>
          </TwoFactorActions>

          <SmsConsentText>{t("twoFactor.smsConsent")}</SmsConsentText>

          <SmsConsentLinks>
            <SmsConsentLink href={ROUTES.TERMS} target="_blank" rel="noopener noreferrer">
              {t("twoFactor.termsOfUse")}
            </SmsConsentLink>
            <SmsConsentLink href={ROUTES.PRIVACY} target="_blank" rel="noopener noreferrer">
              {t("twoFactor.privacyPolicy")}
            </SmsConsentLink>
            <SmsConsentLink href={ROUTES.SMS_POLICY} target="_blank" rel="noopener noreferrer">
              {t("twoFactor.smsMessagingPolicy")}
            </SmsConsentLink>
          </SmsConsentLinks>
        </Box>
      </TwoFactorContainer>
    </AuthPageLayout>
  );
};

const TwoFactorAuthPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Failed to send code. Please try again.",
    });

  // Check if this is registration flow
  const isRegistrationFlow = localStorage.getItem(
    STORAGE_KEYS.REGISTRATION_TOKEN
  );

  const validationRules = {
    phoneNumber: {
      custom: (value: FormFieldValue) => {
        const phoneValue = String(value || "");
        if (!phoneValue) {
          return t("twoFactor.validation.required", "Phone number is required");
        }
        if (!validatePhoneNumber(phoneValue)) {
          return t(
            "twoFactor.validation.invalidFormat",
            "Please enter a valid US phone number"
          );
        }
        return ""; // No error
      },
    },
  };

  const handleSubmit = async (
    formData: FormDataType,
    validationResult: ValidationResult
  ) => {
    // Block submit strictly based on validationRules outcome
    const hasClientErrors =
      !validationResult?.isValid ||
      (Array.isArray(validationResult?.errorMessages) &&
        validationResult.errorMessages.length > 0);
    if (hasClientErrors) return;

    clearErrors(); // Clear previous errors
    setIsSubmitting(true);
    try {
      // Save phone number and send code
      const phoneNumber = cleanPhoneNumber(String(formData.phoneNumber));
      setCookie(COOKIE_KEYS.PHONE_HINT, phoneNumber, 0);

      if (isRegistrationFlow) {
        // For registration flow
        await dispatch(sendRegistrationPhoneCode(phoneNumber)).unwrap();
        navigate(`${ROUTES.VERIFICATION}?redirect=${ROUTES.SUCCESS}`);
      } else {
        // For login flow
        const email = getCookie(COOKIE_KEYS.EMAIL_HINT);
        if (!email) {
          handleApiError(
            "Email not found. Please try again from the previous step."
          );
          setIsSubmitting(false);
          return;
        }
        await dispatch(sendTwoFactorCode(email)).unwrap();
        navigate(`${ROUTES.VERIFICATION}?redirect=${ROUTES.SAVE_BACKUP_CODES}`);
      }
    } catch (errors: unknown) {
      handleApiError(errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      validationRules={validationRules}
      onSubmit={handleSubmit}
      externalErrors={fieldErrors}
      showFieldErrors={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
      }}
    >
      <TwoFactorForm
        isSubmitting={isSubmitting}
        apiErrors={apiErrors}
        fieldErrors={fieldErrors}
      />
    </Form>
  );
};

export default TwoFactorAuthPage;
